"use client";

import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CreditCard,
  DollarSign,
  Heart,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import Link from "next/link";
import { AreaChart } from "@/components/shared/area-chart";
import { PageBody, PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { dashboardApi, type DashboardCardSet, type DashboardSpofCard } from "@/lib/api/dashboard";
import { reportsApi } from "@/lib/api/reports";
import { formatApiError } from "@/lib/api/client";

export default function DashboardPage() {
  const dash = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: dashboardApi.fetchAll,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  // V_CMS_DEEP_REVAMP F26 — 14g trend için reports endpoint'i.
  // Dashboard'da küçük inline trend olarak gösterilir; full panel /reports'ta.
  const trend = useQuery({
    queryKey: ["dashboard", "trend-14d"],
    queryFn: () => reportsApi.dailyUsage(14),
    staleTime: 60_000,
  });

  // V_REPORTS — pivot-destekli özet alanları (edinim / plan geliri / maliyet).
  const acq = useQuery({
    queryKey: ["dashboard", "acq-30d"],
    queryFn: () =>
      reportsApi.pivot({ dataset: "users", group_by: ["signup_date"], measures: ["count"], days: 30 }),
    staleTime: 60_000,
  });
  const revByPlan = useQuery({
    queryKey: ["dashboard", "rev-by-plan"],
    queryFn: () =>
      reportsApi.pivot({ dataset: "subscriptions", group_by: ["plan_id"], measures: ["count", "mrr"] }),
    staleTime: 60_000,
  });
  const costBySvc = useQuery({
    queryKey: ["dashboard", "cost-by-svc-30d"],
    queryFn: () =>
      reportsApi.pivot({ dataset: "cost", group_by: ["service"], measures: ["cost_usd"], days: 30 }),
    staleTime: 60_000,
  });

  const d = dash.data;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="aicontent operasyonel paneli — kullanıcılar, gelir, sistem sağlığı ve maliyet canlı bakış."
        badge={
          <Badge variant="outline" className="font-mono text-[10px]">
            v0.5 · canlı
          </Badge>
        }
      />
      <PageBody>
        <div className="space-y-8">
          {dash.isError ? (
            <div className="rounded-lg border border-error/40 bg-error/5 p-4 text-sm text-error">
              Dashboard verileri yüklenemedi: {formatApiError(dash.error)}
            </div>
          ) : null}

          {/* F11 cluster K — pool SPOF kart bloğu. Anthropic havuzu tek
              anahtara düşmüşse ya da son 24 saatte hata almışsa burada
              kırmızı/turuncu kart belirir. Operatör /api-pool sayfasına
              tek tıkla geçer. */}
          {d?.spof && d.spof.length > 0 ? (
            <section className="space-y-2">
              {d.spof.map((card) => (
                <SpofCard key={card.provider} card={card} />
              ))}
            </section>
          ) : null}

          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              icon={Users}
              label="Aktif kullanıcı"
              value={formatNumber(d?.users?.total)}
              hint={fmtUsersHint(d)}
              tone="blue"
              loading={dash.isLoading}
            />
            <MetricCard
              icon={CreditCard}
              label="Aktif abonelik"
              value={formatNumber(d?.revenue?.active_subscriptions)}
              hint={fmtRevenueHint(d)}
              tone="purple"
              loading={dash.isLoading}
            />
            <MetricCard
              icon={DollarSign}
              label="Maliyet (30g)"
              value={formatUsd(d?.usage?.total_cost_usd)}
              hint={fmtUsageHint(d)}
              tone="green"
              loading={dash.isLoading}
            />
            <MetricCard
              icon={Heart}
              label="Sistem sağlığı"
              value={fmtHealthValue(d)}
              hint={fmtHealthHint(d)}
              tone={healthTone(d)}
              loading={dash.isLoading}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Anomali (24s)"
              value={formatNumber(d?.health?.anomalies_24h)}
              hint={
                d?.health
                  ? `${d.health.recoveries_24h} otomatik kurtarma yapıldı`
                  : "anomaly_events tablosu"
              }
              tone={(d?.health?.anomalies_24h ?? 0) > 0 ? "orange" : "dark"}
              loading={dash.isLoading}
            />
            <MetricCard
              icon={Activity}
              label="Stuck heartbeat"
              value={formatNumber(d?.health?.stale_heartbeats)}
              hint={
                d?.health
                  ? `${d.health.in_flight_generation_jobs} aktif jeneration job`
                  : "/api/admin/recovery/stuck"
              }
              tone={(d?.health?.stale_heartbeats ?? 0) > 0 ? "orange" : "dark"}
              loading={dash.isLoading}
            />
          </section>

          {/* V_CMS_DEEP_REVAMP F26 — 14g trend grafikleri */}
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Card>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="size-3.5 text-indigo-600" />
                    <h3 className="text-sm font-semibold text-text-primary">
                      İçerik üretimi (14g)
                    </h3>
                  </div>
                  <Link
                    href="/reports"
                    className="text-[11px] text-text-tertiary hover:text-accent"
                  >
                    Detay →
                  </Link>
                </div>
                {trend.isLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : (
                  <AreaChart
                    height={140}
                    data={(trend.data?.series ?? []).map((s) => ({
                      date: s.date,
                      value: s.contents,
                    }))}
                    stroke="rgb(99 102 241)"
                    fill="rgb(99 102 241 / 0.12)"
                  />
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="size-3.5 text-emerald-600" />
                    <h3 className="text-sm font-semibold text-text-primary">
                      Maliyet (14g, $)
                    </h3>
                  </div>
                  <Link
                    href="/reports"
                    className="text-[11px] text-text-tertiary hover:text-accent"
                  >
                    Detay →
                  </Link>
                </div>
                {trend.isLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : (
                  <AreaChart
                    height={140}
                    data={(trend.data?.series ?? []).map((s) => ({
                      date: s.date,
                      value: s.cost_usd,
                    }))}
                    stroke="rgb(16 185 129)"
                    fill="rgb(16 185 129 / 0.12)"
                  />
                )}
              </CardContent>
            </Card>
          </section>

          {/* V_REPORTS — pivot özet alanları */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-base font-semibold text-text-primary">
                Rapor özetleri
              </h2>
              <a
                href="/reports/custom"
                className="text-xs font-medium text-indigo-600 hover:underline"
              >
                Özel rapor oluştur →
              </a>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {/* Kullanıcı edinimi */}
              <Card>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-sm font-medium text-text-primary">
                      Kullanıcı edinimi (30g)
                    </span>
                    <span className="font-display text-lg font-semibold tabular-nums text-text-primary">
                      {(acq.data?.rows ?? []).reduce((s, r) => s + Number(r.count ?? 0), 0)}
                    </span>
                  </div>
                  <AreaChart
                    height={120}
                    data={(acq.data?.rows ?? []).map((r) => ({
                      date: String(r.signup_date ?? ""),
                      value: Number(r.count ?? 0),
                    }))}
                    stroke="rgb(16 185 129)"
                    fill="rgb(16 185 129 / 0.12)"
                  />
                </CardContent>
              </Card>

              {/* Plan geliri */}
              <Card>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-sm font-medium text-text-primary">Plan geliri (MRR)</span>
                    <span className="font-display text-lg font-semibold tabular-nums text-emerald-600">
                      ${(revByPlan.data?.rows ?? []).reduce((s, r) => s + Number(r.mrr ?? 0), 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {(revByPlan.data?.rows ?? []).slice(0, 6).map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="truncate text-text-secondary">
                          {String(r.plan_id__label ?? r.plan_id ?? "—")}
                        </span>
                        <span className="shrink-0 tabular-nums text-text-tertiary">
                          {Number(r.count ?? 0)} abone · ${Number(r.mrr ?? 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {!revByPlan.data?.rows?.length ? (
                      <p className="text-xs text-text-tertiary">Aktif abonelik yok.</p>
                    ) : null}
                  </div>
                </CardContent>
              </Card>

              {/* Maliyet — servis bazlı */}
              <Card>
                <CardContent className="p-4">
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-sm font-medium text-text-primary">Maliyet · servis (30g)</span>
                    <span className="font-display text-lg font-semibold tabular-nums text-amber-600">
                      ${(costBySvc.data?.rows ?? []).reduce((s, r) => s + Number(r.cost_usd ?? 0), 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {(costBySvc.data?.rows ?? []).slice(0, 6).map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="truncate text-text-secondary">{String(r.service ?? "—")}</span>
                        <span className="shrink-0 tabular-nums text-text-tertiary">
                          ${Number(r.cost_usd ?? 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {!costBySvc.data?.rows?.length ? (
                      <p className="text-xs text-text-tertiary">Maliyet kaydı yok.</p>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <section>
            <h2 className="mb-3 font-display text-base font-semibold text-text-primary">
              Hızlı erişim
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <QuickCard
                href="/reports"
                title="Raporlar"
                desc="Günlük usage, sektör performansı, plan funnel, sağlık trendi."
                icon={BarChart3}
              />
              <QuickCard
                href="/users"
                title="Kullanıcı yönetimi"
                desc="Plan değişikliği, askıya alma, kullanım & maliyet detayı."
                icon={Users}
              />
              <QuickCard
                href="/master-prompts"
                title="Master prompt kütüphanesi"
                desc="Sektör × hizmet × içerik tipi matrisi."
                icon={Sparkles}
              />
              <QuickCard
                href="/external-services"
                title="API anahtar yönetimi"
                desc="DeepSeek, SerpAPI, Sentry, Supabase + her biri için test butonu."
                icon={Heart}
              />
              <QuickCard
                href="/payment-providers"
                title="Ödeme sağlayıcıları"
                desc="Aktif provider (Paddle/Stripe/PayPal) — tek tık switch + config readiness."
                icon={Heart}
              />
              <QuickCard
                href="/system-settings"
                title="Sistem ayarları"
                desc="Pricing, threshold, quality gate, banned phrases."
                icon={Heart}
              />
              <QuickCard
                href="/recovery"
                title="Recovery"
                desc="Stuck content sweep, anomaly events, heartbeat stale."
                icon={AlertTriangle}
              />
            </div>
          </section>
        </div>
      </PageBody>
    </>
  );
}

function SpofCard({ card }: { card: DashboardSpofCard }) {
  const isCritical = card.level === "critical";
  return (
    <Link
      href="/api-pool"
      className={`group flex items-start gap-3 rounded-lg border p-4 transition-colors ${
        isCritical
          ? "border-error/50 bg-error/5 hover:bg-error/10"
          : "border-warning/50 bg-warning/5 hover:bg-warning/10"
      }`}
    >
      <ShieldAlert
        className={`mt-0.5 size-5 shrink-0 ${
          isCritical ? "text-error" : "text-warning"
        }`}
      />
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">
            {card.provider.toUpperCase()} havuzu — {isCritical ? "kritik" : "uyarı"}
          </span>
          <Badge
            variant="outline"
            className={`font-mono text-[10px] ${
              isCritical ? "border-error/40 text-error" : "border-warning/40 text-warning"
            }`}
          >
            {card.active_keys}/{card.min_recommended} aktif
          </Badge>
        </div>
        <p className="text-xs leading-snug text-text-secondary">{card.message}</p>
        <p className="text-[11px] uppercase tracking-wide text-text-tertiary opacity-80 group-hover:opacity-100">
          API Pool sayfasına git →
        </p>
      </div>
    </Link>
  );
}

function formatNumber(v: number | undefined | null): string | null {
  if (v === undefined || v === null) return null;
  return new Intl.NumberFormat("tr-TR").format(v);
}

function formatUsd(v: number | undefined | null): string | null {
  if (v === undefined || v === null) return null;
  return `$${v.toFixed(2)}`;
}

function fmtUsersHint(d: DashboardCardSet | undefined): string {
  if (!d?.users) return "/api/admin/analytics/users";
  const { new_last_30d, suspended, setup_completed } = d.users;
  return `+${new_last_30d} (30g) · setup ✓ ${setup_completed} · askıda ${suspended}`;
}

function fmtRevenueHint(d: DashboardCardSet | undefined): string {
  if (!d?.revenue) return "/api/admin/analytics/revenue";
  return `MRR $${d.revenue.mrr_usd.toFixed(0)} · ARR $${d.revenue.arr_usd.toFixed(0)}`;
}

function fmtUsageHint(d: DashboardCardSet | undefined): string {
  if (!d?.usage) return "/api/admin/analytics/usage";
  const { contents_ready, contents_created, total_calls } = d.usage;
  return `${contents_ready}/${contents_created} içerik · ${total_calls} AI çağrısı`;
}

function fmtHealthValue(d: DashboardCardSet | undefined): string | null {
  if (!d?.health) return null;
  const issues =
    (d.health.stale_heartbeats > 0 ? 1 : 0) +
    (d.health.anomalies_24h > 0 ? 1 : 0);
  return issues === 0 ? "Sağlıklı" : `${issues} uyarı`;
}

function fmtHealthHint(d: DashboardCardSet | undefined): string {
  if (!d?.health?.checked_at) return "/api/admin/health/detailed";
  const dt = new Date(d.health.checked_at);
  return `${dt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} kontrol edildi`;
}

function healthTone(d: DashboardCardSet | undefined): "orange" | "dark" {
  if (!d?.health) return "dark";
  if (d.health.stale_heartbeats > 0 || d.health.anomalies_24h > 0) return "orange";
  return "dark";
}

function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
  tone,
  loading,
}: {
  icon: typeof Users;
  label: string;
  value: string | null;
  hint: string;
  tone: "blue" | "purple" | "green" | "orange" | "dark";
  loading: boolean;
}) {
  const toneClass: Record<typeof tone, string> = {
    blue: "bg-card-blue-bg text-card-blue",
    purple: "bg-card-purple-bg text-card-purple",
    green: "bg-card-green-bg text-card-green",
    orange: "bg-card-orange-bg text-card-orange",
    dark: "bg-card-dark text-card-dark-fg",
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {label}
          </span>
          <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[tone]}`}>
            <Icon className="h-4 w-4" />
          </span>
        </div>
        <div>
          {loading || value === null ? (
            <Skeleton className="h-7 w-20" />
          ) : (
            <p className="font-display text-2xl font-semibold tabular-nums text-text-primary">
              {value}
            </p>
          )}
          <p className="mt-1 text-[10px] text-text-tertiary">{hint}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickCard({
  href,
  title,
  desc,
  icon: Icon,
}: {
  href: string;
  title: string;
  desc: string;
  icon: typeof Users;
}) {
  return (
    <Link
      href={href}
      className="group block rounded-lg border border-border-subtle bg-bg-card p-5 transition-all hover:border-accent/40 hover:shadow-md"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-light text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <CardTitle className="text-sm font-semibold text-text-primary">{title}</CardTitle>
          <CardDescription className="text-xs leading-snug text-text-secondary">
            {desc}
          </CardDescription>
        </div>
      </div>
    </Link>
  );
}
